import { useEffect, useState } from 'react';
import { useAuth, ShellAuthProvider } from '@aimsos/auth';
import { WorkspacesApp } from '@aimsos/workspaces';
import { registerNamespace, i18n } from '../i18n/bootstrap.js';

/**
 * Mounts the Workspaces application inside the Core Shell.
 * Mirrors the same embedding pattern as DashboardsEmbed.
 *
 * Reads from the unified useAuth() (backed by our own backend JWT) instead
 * of useAuth0() directly, so this still mounts correctly on a
 * {slug}.{rootDomain} subdomain landed on via a cross-subdomain handoff,
 * where Auth0 SDK itself has no session.
 */
const WorkspacesEmbed = () => {
  const { user, token, isAuthenticated, getAccessTokenSilently } = useAuth();
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('@aimsos/workspaces/i18n/register')
      .then(({ registerWorkspacesI18n }) => {
        registerWorkspacesI18n(registerNamespace);
        return i18n.loadNamespaces('workspaces');
      })
      .then(() => {
        if (!cancelled) setI18nReady(true);
      })
      .catch((err) => console.error('[WorkspacesEmbed] i18n:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    window.__AIMS_SHELL__ = {
      mounted: true,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlWS:
        import.meta.env.VITE_API_BASE_URL_WS || import.meta.env.VITE_API_BASE_URL,
      apiBaseUrlAuth: import.meta.env.VITE_API_BASE_URL_AUTH,
    };
    return () => {
      delete window.__AIMS_SHELL__;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !token || !i18nReady) return null;

  return (
    <ShellAuthProvider
      token={token}
      user={user}
      isAuthenticated={isAuthenticated}
      getAccessTokenSilently={getAccessTokenSilently}
    >
      <WorkspacesApp />
    </ShellAuthProvider>
  );
};

export default WorkspacesEmbed;
